import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  DateLabel,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PayinProviderConversion, PayinProviderConversionRow } from 'types';
import { formatUtils, p2pProviderUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PayinProviderConversion>;
};

const EMPTY_VALUE = '—';

export const ProviderConversionTable: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.providers',
  });

  const rowsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.items,
      } as UseQueryResult<PayinProviderConversionRow[]>),
    [queryResult],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<PayinProviderConversionRow>[] => [
      {
        header: t('columns.p2p_provider'),
        valueGetter: (item) => item.providerName || EMPTY_VALUE,
      },
      {
        header: t('columns.shop'),
        valueGetter: (item) => item.shopName || EMPTY_VALUE,
      },
      {
        header: t('columns.provider_type'),
        valueGetter: (item) =>
          item.providerType
            ? p2pProviderUtils.getTypeLabel(item.providerType)
            : EMPTY_VALUE,
      },
      {
        header: t('columns.considered_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.consideredCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.sent_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.sentCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.requisites_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.requisitesCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.cascade_lost_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.cascadeLostCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.completed_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.completedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.not_completed_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.notCompletedCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.in_progress_count'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.inProgressCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('columns.requisites_conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.requisitesConversion} unit="%" />
        ),
      },
      {
        header: t('columns.completed_conversion'),
        // Everything the provider returned went to someone else: there is nothing to convert, and a
        // bare 0% would read as the provider failing to close orders it never had
        valueGetter: (item) =>
          item.requisitesCount > item.cascadeLostCount ? (
            <StylizedNumber value={item.completedConversion} unit="%" />
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        header: t('columns.end_to_end_conversion'),
        valueGetter: (item) => (
          <StylizedNumber value={item.endToEndConversion} unit="%" />
        ),
      },
      {
        header: t('columns.first_order'),
        valueGetter: (item) =>
          item.firstOrderAt ? (
            <DateLabel>{formatUtils.formatDate(item.firstOrderAt)}</DateLabel>
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        header: t('columns.last_order'),
        valueGetter: (item) =>
          item.lastOrderAt ? (
            <DateLabel>{formatUtils.formatDate(item.lastOrderAt)}</DateLabel>
          ) : (
            EMPTY_VALUE
          ),
      },
    ],
    [t, tCommon],
  );

  return <CrudTable queryResult={rowsQueryResult} columns={columns} />;
};
